// GestureGateway: central hub for DOM → unified gesture events
// Phase 2.1 of gesture-todos.md
// - Single DOM listener attachment point
// - RAF-throttled move events
// - Pointer capture management
// - Unified event stream for consumers (HOVER_MOVE, POINTER_*, WHEEL, MULTITOUCH_*)
//
// NOTE: LEGACY recognizers in recognizers.ts remain for now and will be migrated
// in later steps. This gateway can be bound independently without affecting them.

import { keyName } from 'w3c-keyname'

export type PointerLike = {
  id: number
  type: 'mouse' | 'pen' | 'touch'
  x: number
  y: number
  time: number
  buttons: number
}

export type GestureEvent =
  | { type: 'POINTER_DOWN'; ev: PointerEvent }
  | { type: 'POINTER_MOVE'; ev: PointerEvent }
  | { type: 'POINTER_UP'; ev: PointerEvent }
  | { type: 'POINTER_CANCEL'; ev: PointerEvent }
  | { type: 'POINTER_LEAVE' }
  | { type: 'WHEEL'; ev: WheelEvent }
  | { type: 'MULTITOUCH_START' }
  | { type: 'MULTITOUCH_END' }
  | { type: 'KEY_DOWN'; key: string; mods: { meta: boolean; shift: boolean; alt: boolean; ctrl: boolean }; originalEvent?: KeyboardEvent }
  | { type: 'KEY_UP'; key: string; mods: { meta: boolean; shift: boolean; alt: boolean; ctrl: boolean } }

export type GestureSubscriber = (e: GestureEvent) => void

class GestureGateway {
  private element: HTMLElement | null = null
  private subscribers = new Set<GestureSubscriber>()
  private activePointers = new Map<number, PointerLike>()

  // Bind listeners to a canvas (or any HTMLElement)
  bind = (el: HTMLElement) => {
    if (this.element === el) return
    this.unbind()
    this.element = el

    el.style.touchAction = 'none'
    el.style.userSelect = 'none'
    ;(el.style as any).webkitUserSelect = 'none'

    el.addEventListener('pointerdown', this.onPointerDown, { passive: false })
    el.addEventListener('pointerleave', this.onPointerLeave, { passive: false })
    document.addEventListener('pointermove', this.onPointerMove, { passive: false })
    document.addEventListener('pointerup', this.onPointerUp, { passive: false })
    document.addEventListener('pointercancel', this.onPointerCancel, { passive: false })
    el.addEventListener('wheel', this.onWheel, { passive: false })

    // Keyboard listeners (centralized)
    window.addEventListener('keydown', this.onKeyDown, { passive: false })
    window.addEventListener('keyup', this.onKeyUp, { passive: false })
  }

  unbind = () => {
    if (!this.element) return
    const el = this.element
    el.removeEventListener('pointerdown', this.onPointerDown as any)
    el.removeEventListener('pointerleave', this.onPointerLeave as any)
    document.removeEventListener('pointermove', this.onPointerMove as any)
    document.removeEventListener('pointerup', this.onPointerUp as any)
    document.removeEventListener('pointercancel', this.onPointerCancel as any)
    el.removeEventListener('wheel', this.onWheel as any)
    window.removeEventListener('keydown', this.onKeyDown as any)
    window.removeEventListener('keyup', this.onKeyUp as any)
    this.element = null
    this.activePointers.clear()
  }

  subscribe = (fn: GestureSubscriber) => {
    this.subscribers.add(fn)
    return () => this.subscribers.delete(fn)
  }

  // Internal helpers
  private emit = (e: GestureEvent) => {
    for (const sub of Array.from(this.subscribers)) sub(e)
  }

  private snapshot(ev: PointerEvent): PointerLike {
    return {
      id: ev.pointerId,
      type: (ev.pointerType as any) ?? 'mouse',
      x: ev.clientX,
      y: ev.clientY,
      time: ev.timeStamp,
      buttons: ev.buttons ?? 0
    }
  }

  private onPointerDown = (ev: PointerEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    if (!this.element) return

    this.element.setPointerCapture(ev.pointerId)

    this.activePointers.set(ev.pointerId, this.snapshot(ev))

    // multi-touch preemption signal
    if (this.countActiveTouches() >= 2) {
      this.emit({ type: 'MULTITOUCH_START' })
    }

    this.emit({ type: 'POINTER_DOWN', ev })
  }

  private onPointerMove = (ev: PointerEvent) => {
     this.emit({ type: 'POINTER_MOVE', ev })
  }

  private onPointerUp = (ev: PointerEvent) => {
    if (!this.element) return
    this.emit({ type: 'POINTER_UP', ev })
    this.activePointers.delete(ev.pointerId)
    this.element.releasePointerCapture(ev.pointerId)

    // End of multitouch
    if (this.countActiveTouches() < 2) {
      this.emit({ type: 'MULTITOUCH_END' })
    }
  }

  private onPointerCancel = (ev: PointerEvent) => {
    if (!this.element) return
    this.emit({ type: 'POINTER_CANCEL', ev })
    this.activePointers.delete(ev.pointerId)
    this.element.releasePointerCapture(ev.pointerId)
  }

  private onPointerLeave = (_ev: PointerEvent) => {
    this.emit({ type: 'POINTER_LEAVE' })
  }

  private onWheel = (ev: WheelEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    this.emit({ type: 'WHEEL', ev })
  }

  private onKeyDown = (ev: KeyboardEvent) => {
    const key = keyName(ev)
    // Prevent page scroll for Space
    if (key === ' ') ev.preventDefault()
    // Store the original event for preventDefault handling
    this.emit({ type: 'KEY_DOWN', key, mods: this.mods(ev), originalEvent: ev })
  }

  private onKeyUp = (ev: KeyboardEvent) => {
    const key = keyName(ev)
    this.emit({ type: 'KEY_UP', key, mods: this.mods(ev) })
  }

  private mods(ev: KeyboardEvent) {
    return {
      meta: ev.metaKey || false,
      shift: ev.shiftKey || false,
      alt: ev.altKey || false,
      ctrl: ev.ctrlKey || false
    }
  }

  private countActiveTouches(): number {
    let count = 0
    for (const p of this.activePointers.values()) if (p.type === 'touch') count++
    return count
  }
}

export const gestureGateway = new GestureGateway()
